import config from '../config';
import { ACL_CONTEXT_HEADER } from '../utils/aclContext';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Client for the REAL Mia-Platform Context Catalog's AI Playbooks
 * (mia-flow-bff's `GET /api/catalog/ai-playbooks`, routes/mia-platform-
 * data-v2.js) — the same Catalog family (`ai.mia-platform.eu`/`playbooks`)
 * AI Foundry's own Playbook management page reads from. Used for two things:
 * listing every playbook so the user can pick one (PlaybookPicker.jsx), and
 * picking the best match for a piece of free text ("Auto").
 *
 * Deliberately NOT adk-be-app's own `GET /api/playbooks` (proxied via
 * `/api/adk-proxy/*`) — that route falls back to adk-be-app's local seeded
 * Postgres mock data whenever ITS OWN `CATALOG_API_URL` is unset/unreachable,
 * which silently showed a different, smaller "(test bed)" mock list here
 * than the real Catalog. Going straight to mia-flow-bff's own Catalog routes
 * matches this app's established rule for AI Playbooks (task history: "the
 * ai-playbooks must be taken from context catalog like ai-foundry do").
 */

let cachedPlaybooks = null;
let cachedAt = 0;
let cachedScope = null;
const CACHE_TTL_MS = 60_000;

/**
 * Every playbook in the Catalog, mapped to `{name, displayName, description,
 * tags}[]`. Cached in-memory for a minute — this is called once per
 * VfsChatPanel mount (every project/service switch), and the catalog
 * doesn't change that often.
 */
export async function listPlaybooks({ force = false } = {}) {
  // Authorized fetch + getBffHeaders: the Catalog scopes playbooks by the selected
  // org/tenant in x-mia-acl-context and answers 403 when the header is missing.
  // The cache is per scope, so switching org never shows the previous org's list.
  const headers = getBffHeaders();
  const scope = headers[ACL_CONTEXT_HEADER] || '';
  if (!force && cachedPlaybooks && cachedScope === scope && Date.now() - cachedAt < CACHE_TTL_MS) {
    return cachedPlaybooks;
  }
  const base = (config.bffUrl || '').replace(/\/+$/, '');
  const response = await getAuthorizedFetch()(`${base}/api/catalog/ai-playbooks`, { headers });
  if (!response.ok) {
    throw new Error(`Failed to list playbooks (${response.status})`);
  }
  const data = await response.json();
  const playbooks = (data.data?.items || []).map((p) => ({
    name: p.itemId || p.id || p.name,
    displayName: p.name || p.itemId || p.id,
    description: p.description || '',
    tags: [],
  }));
  cachedPlaybooks = playbooks;
  cachedAt = Date.now();
  cachedScope = scope;
  return playbooks;
}

/**
 * Server-side "Auto" playbook selection (task #194) — asks mia-flow-bff's
 * `POST /api/mia-platform/ai-playbooks/select` (routes/mia-platform-data.js)
 * to classify `text` against the REAL Catalog playbook list (it fetches that
 * list via the same in-process `fetchAiPlaybooksList` function `GET
 * /api/catalog/ai-playbooks` uses, then calls adk-be-app's LLM classifier
 * only to pick among THAT real list — not adk-be-app's own possibly-mocked
 * list).
 *
 * mia-flow-bff's `selectAiPlaybook` (lib/playbook-selection.js) now ALWAYS
 * returns a real, usable `selected` — a deterministic default (a real Catalog
 * playbook by convention name, or a hardcoded stand-in if even that's
 * missing) when nothing confidently matched, never a bare null — so this no
 * longer needs its OWN separate client-side guess (the `pickBestPlaybook`
 * keyword-overlap heuristic below) as a second opinion that could disagree
 * with the server's answer. `fallbackName` is used only if the BFF call
 * itself couldn't be reached at all (network failure) — a real fallback
 * chain still lives entirely server-side; this is just "couldn't ask".
 */
export async function selectPlaybookServerSide(playbooks, text, fallbackName = 'code-generation-playbook') {
  const base = (config.bffUrl || '').replace(/\/+$/, '');
  try {
    const response = await getAuthorizedFetch()(`${base}/api/mia-platform/ai-playbooks/select`, {
      method: 'POST',
      headers: getBffHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ prompt: text }),
    });
    if (!response.ok) {
      throw new Error(`Failed to select playbook (${response.status})`);
    }
    const data = await response.json();
    if (!data.success || !data.selected) {
      return fallbackName;
    }
    return data.selected.itemId || data.selected.id || data.selected.name || fallbackName;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('⚠️ Could not reach mia-flow-bff for playbook selection, using fallback:', error);
    return fallbackName;
  }
}

const STOPWORDS = new Set([
  'a', 'an', 'the', 'to', 'of', 'for', 'and', 'or', 'in', 'on', 'with', 'is',
  'are', 'be', 'this', 'that', 'i', 'me', 'my', 'want', 'need', 'please',
  'can', 'you', 'it', 'do', 'does', 'how', 'what', 'help', 'create', 'make',
]);

function tokenize(text) {
  return (text || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2 && !STOPWORDS.has(word));
}

/**
 * Picks the playbook whose display name/description/tags share the most
 * distinct words with `text` (a plain keyword-overlap score, not a model
 * call — instant, deterministic, no extra request). Falls back to
 * `fallbackName` (Flow's own historical default, "code-generation-playbook")
 * when nothing scores above a token of overlap, since a 0-overlap "best
 * match" is not actually a match — it would just be whichever playbook
 * happens to sort first.
 */
export function pickBestPlaybook(playbooks, text, fallbackName = 'code-generation-playbook') {
  const queryWords = new Set(tokenize(text));
  if (queryWords.size === 0 || !playbooks?.length) {
    return fallbackName;
  }

  let best = null;
  let bestScore = 0;
  for (const playbook of playbooks) {
    const haystack = tokenize(
      `${playbook.displayName} ${playbook.description} ${(playbook.tags || []).join(' ')}`
    );
    const haystackWords = new Set(haystack);
    let score = 0;
    for (const word of queryWords) {
      if (haystackWords.has(word)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = playbook.name;
    }
  }
  return best || fallbackName;
}
